/**
 * Codigos de error del dominio (ADR-034).
 *
 * Son estables y en mayusculas: la interfaz decide que mostrar **por codigo**, nunca
 * comparando textos. Cambiar un codigo es un cambio incompatible.
 *
 * SEGURIDAD (docs/SEGURIDAD.md §6): un `ErrorDominio` **no puede transportar valores**.
 * Solo lleva el codigo y, como mucho, el NOMBRE del campo que fallo. Nunca el valor del
 * campo. Eso hace estructuralmente imposible que una cedula, un telefono o un importe se
 * filtren a un log, a un mensaje de error o a una respuesta HTTP.
 */
/** Todos los codigos de error que el dominio puede devolver. */
export const CODIGOS_ERROR = [
    // ── Dinero ────────────────────────────────────────────────────────────────
    'MONTO_INVALIDO',
    'DESBORDE_MONETARIO',
    'DIVISOR_INVALIDO',
    // ── Tiempo ────────────────────────────────────────────────────────────────
    'INSTANTE_INVALIDO',
    'FECHA_LOCAL_INVALIDA',
    'HORA_LOCAL_INVALIDA',
    'HORA_LOCAL_INEXISTENTE',
    'ZONA_INVALIDA',
    // ── Agenda ────────────────────────────────────────────────────────────────
    'DURACION_INVALIDA',
    'INICIO_NO_ALINEADO',
    'CANCHA_INVALIDA',
    'SLOT_OCUPADO',
    /** El fin es anterior o igual al comienzo (Fase 10). */
    'RANGO_INVERTIDO',
    /** El rango no contiene ningun slot: no hay nada que bloquear (Fase 10). */
    'RANGO_VACIO',
    /** El bloqueo abarca mas slots de los que admite una sola transaccion (ADR-051). */
    'MAXIMO_SLOTS_EXCEDIDO',
    // ── Recurrentes (Fase 10, ADR-049) ────────────────────────────────────────
    /** La serie generaria mas ocurrencias que el tope documentado. */
    'MAXIMO_OCURRENCIAS_EXCEDIDO',
    /** Frecuencia `fechasTemporada` sin ninguna fecha explicita utilizable. */
    'TEMPORADA_SIN_FECHAS',
    /** La serie no genera ninguna ocurrencia valida: no hay nada que crear. */
    'SERIE_SIN_OCURRENCIAS',
    // ── Tarifas ───────────────────────────────────────────────────────────────
    'TARIFA_INEXISTENTE',
    'TABLA_TARIFAS_INCOMPLETA',
    'TABLA_TARIFAS_SUPERPUESTA',
    'TABLA_TARIFAS_VACIA',
    // ── Ventana publica ───────────────────────────────────────────────────────
    'FUERA_DE_VENTANA',
    'ANTICIPACION_INSUFICIENTE',
    'PARAMETRO_INVALIDO',
    // ── Clientes ──────────────────────────────────────────────────────────────
    'CEDULA_INVALIDA',
    'CEDULA_DUPLICADA',
    'TELEFONO_INVALIDO',
    // ── Reservas ──────────────────────────────────────────────────────────────
    'TRANSICION_INVALIDA',
    'EVENTO_FUERA_DE_ORDEN',
    'HISTORIAL_VACIO',
    'RESERVA_NO_CANCELABLE',
    // ── Cuenta corriente ──────────────────────────────────────────────────────
    'MOVIMIENTO_INVALIDO',
    'SALDO_DIVERGENTE',
    'SALDO_INSUFICIENTE',
    'PAGO_VACIO',
    'PAGO_INCONSISTENTE',
    /** Falta el motivo escrito que exige una operacion excepcional (Fase 08). */
    'MOTIVO_REQUERIDO',
    /** Un empleado intento un ajuste por encima de su tope (ADR-048). */
    'LIMITE_AJUSTE_EXCEDIDO',
    /** El pago ya fue revertido: una segunda reversion duplicaria el credito. */
    'PAGO_YA_REVERTIDO',
    /** El cliente esta bloqueado para tomar reservas nuevas (ADR-048). */
    'CLIENTE_BLOQUEADO',
    // ── Productos, stock y ventas (Fase 11B) ─────────────────────────────────
    'STOCK_INSUFICIENTE',
    'PRODUCTO_INACTIVO',
    'PRODUCTO_INEXISTENTE',
    'SKU_DUPLICADO',
    'SKU_INVALIDO',
    'CANTIDAD_INVALIDA',
    'MONEDA_INVALIDA',
    'CLIENTE_REQUERIDO',
    'VENTA_NO_ANULABLE',
    'VENTA_VACIA',
    'VENTA_INCONSISTENTE',
    'INVENTARIO_ABIERTO',
    /** La categoria indicada no existe o esta desactivada. */
    'CATEGORIA_INEXISTENTE',
    /** Una venta o una compra trae mas lineas de las que admite una transaccion. */
    'MAXIMO_LINEAS_EXCEDIDO',
    /** La venta ya fue anulada, o ya se devolvio entera: no admite otra devolucion. */
    'VENTA_NO_DEVOLVIBLE',
    /** Se intento devolver mas unidades de las que esa venta tiene, o de otro producto. */
    'DEVOLUCION_EXCEDE_VENTA',
    // ── Caja y permisos (se usan a partir de la Fase 3) ────────────────────────
    'CAJA_CERRADA',
    'CAJA_YA_ABIERTA',
    /** La sesion alcanzo el tope de movimientos: hay que cerrarla y abrir otra (ADR-052). */
    'CAJA_LLENA',
    /** Se intento operar sobre una sesion ya cerrada, que es inmutable (ADR-052). */
    'SESION_CERRADA',
    /** Un empleado intento una correccion por encima de su tope (ADR-052). */
    'LIMITE_CORRECCION_EXCEDIDO',
    /** El movimiento ya tiene su contramovimiento: corregirlo dos veces lo duplicaria. */
    'MOVIMIENTO_YA_CORREGIDO',
    /** El esperado guardado no coincide con la suma de los movimientos: hay que mirarlo. */
    'ARQUEO_DIVERGENTE',
    'CUOTA_EXCEDIDA',
    'CODIGO_INVALIDO',
    'PERMISO_DENEGADO',
    // ── Paquetes prepagos (ADR-080, ADR-081) ──────────────────────────────────
    /** La oferta pedida no existe. */
    'OFERTA_PAQUETE_NO_ENCONTRADA',
    /** La oferta existe pero no esta activa: no se puede vender. */
    'OFERTA_PAQUETE_INACTIVA',
    /** La oferta cambio de version desde que se mostro: hay que volver a mirarla. */
    'OFERTA_PAQUETE_DESACTUALIZADA',
    /** Faltan canchas o franjas para poder activar la oferta. */
    'OFERTA_PAQUETE_INCOMPLETA',
    /** La compra de paquete indicada no existe. */
    'PAQUETE_NO_ENCONTRADO',
    /** El paquete pertenece a otro cliente. */
    'PAQUETE_DE_OTRO_TITULAR',
    /** El turno no cumple las condiciones del paquete (cancha, duracion, dia, horario, vigencia). */
    'PAQUETE_NO_APLICA',
    /** El paquete no tiene partidos disponibles para ese turno. */
    'PAQUETE_SIN_PARTIDOS',
    /** La reserva esta cubierta por un paquete: no se cobra ni se configura su cobro. */
    'RESERVA_CUBIERTA_POR_PAQUETE',
    /** La reserva tiene bonificacion, descuento, serie o cobertura: no admite paquete. */
    'RESERVA_NO_ADMITE_PAQUETE',
    /** El traslado de una reserva cubierta necesita que se elija que pasa con el destino. */
    'TRASLADO_PAQUETE_REQUIERE_DECISION',
    /** El efecto de la politica del paquete cambio desde la vista previa. */
    'POLITICA_PAQUETE_CAMBIO',
    /** El pago corresponde a una venta de paquete: se devuelve con un reintegro. */
    'PAGO_DE_PAQUETE',
    /** Se pidio reintegrar mas partidos de los disponibles y reintegrables. */
    'REINTEGRO_EXCEDE_DISPONIBLES',
    /** El monto del reintegro cambio desde que se mostro. */
    'REINTEGRO_DESACTUALIZADO',
    /** La extension no alarga el vencimiento de ningun partido. */
    'EXTENSION_INVALIDA',
    /** El ajuste del paquete no es valido (cantidad, tope o estado). */
    'AJUSTE_PAQUETE_INVALIDO',
];
/** Construye un error del dominio. */
export function errorDominio(codigo, campo) {
    // Con exactOptionalPropertyTypes, `{ campo: undefined }` no es lo mismo que omitirlo.
    return campo === undefined ? { codigo } : { codigo, campo };
}
/** Comprueba si un texto es un codigo de error conocido. */
export function esCodigoErrorDominio(valor) {
    return CODIGOS_ERROR.includes(valor);
}
//# sourceMappingURL=errores.js.map